import { track } from "@connect/observability";
import type { PostMessageClient } from "@connect/post-message/client";
import type { ConnectMessageMap } from "@connect/post-message/protocol";
import {
  type ActiveConnection,
  type Connection,
  type ConnectionError,
  ConnectionErrorCode,
  ConnectionStatus,
  type FailedConnection,
  type PendingConnection,
  type TermsAcceptanceRequiredConnection,
  type UnavailableConnection,
} from "@moonpay/platform-protocol/models/connection";
import { flushTerminal } from "../../lib/flush-terminal.ts";
import { trackFrameReady } from "../../lib/track-frame-ready.ts";
import type {
  ActiveMessageArgs,
  MessageMeta,
  PendingMessageArgs,
} from "../../lib/external-messages.types.ts";

// The auth frame only ever completes as active / terms / pending / unavailable —
// never `connectionRequired` (post-OTP that would be circular) and never
// `failed` (terminal failures go out as a generic `error`, not a `complete`).
type AuthCompletion = Exclude<Connection, FailedConnection>;

/**
 * Every frame -> parent message the auth frame can send. Each method builds a
 * payload typed against the post-message protocol and emits the observability
 * event alongside the send, so the curated event and the wire payload stay in
 * lockstep. Mirrors check-connection: sends go out through the handshake-aware
 * client's `send`, which queues until the ack lands (or logs if it never does)
 * instead of firing blind.
 *
 * The completion methods (active / connectionRequired / pending / unavailable /
 * termsAcceptanceRequired / connectionFailedGeneric) form the shared
 * {@link ConnectionEmitter} surface that {@link routeAuthorize} drives, so the
 * authorize-status routing is identical to the check-connection frame.
 */
export function createExternalMessages(send: PostMessageClient<ConnectMessageMap>["send"]) {
  const complete = async (payload: AuthCompletion, meta: MessageMeta) => {
    track.info("connect.auth.complete", { connectionStatus: payload.status, ...meta });
    // Flush before sending: the embedder may unmount on complete, and WebKit
    // drops the in-flight keepalive POST when an RN WebView unmounts.
    await flushTerminal();
    send("complete", payload);
  };

  const fail = async (payload: ConnectionError, meta: MessageMeta) => {
    track.error("connect.auth.error", { code: payload.code, ...meta });
    await flushTerminal();
    send("error", payload);
  };

  return {
    // The frame has loaded and dispatched the customer's first OTP. Lets the
    // parent reveal the frame / drop a loading state. Mid-flow, so no flush.
    ready(meta: MessageMeta = {}) {
      track.info("connect.auth.ready", meta);
      trackFrameReady("auth");
      send("ready");
    },

    active({ credentials, capabilities, customerId, meta = {} }: ActiveMessageArgs) {
      const payload: ActiveConnection = {
        status: ConnectionStatus.active,
        credentials,
        customer: { id: customerId },
        capabilities,
      };
      return complete(payload, meta);
    },

    pending({ credentials, capabilities, customerId, meta = {} }: PendingMessageArgs) {
      const payload: PendingConnection = {
        status: ConnectionStatus.pending,
        credentials,
        customer: { id: customerId },
        capabilities,
      };
      return complete(payload, meta);
    },

    unavailable(meta: MessageMeta = {}) {
      const payload: UnavailableConnection = { status: ConnectionStatus.unavailable };
      return complete(payload, meta);
    },

    termsAcceptanceRequired(meta: MessageMeta = {}) {
      const payload: TermsAcceptanceRequiredConnection = {
        status: ConnectionStatus.termsAcceptanceRequired,
      };
      return complete(payload, meta);
    },

    connectionFailedGeneric(message: string, meta: MessageMeta = {}) {
      const payload: ConnectionError = {
        code: ConnectionErrorCode.connectionFailedGeneric,
        message,
      };
      return fail(payload, meta);
    },

    close() {
      send("close");
    },
  };
}
